import { AttendanceGuage, ClassCard, MainContainer, TabBar, TabItem } from '@wot/components';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import xhr from '../../utils/fetch';
import { IPlanAttendance } from '.';
import { DEFAULT_PROFILE_PIC } from '../../assets/icons';
import { getAttendacePercentage } from '../../utils/functions';
import { useTab } from '../../hooks/useTab';
import { ISessionAttendance, ITrialClass } from '@wot/typings';
import { LOADING } from '../../assets/animated';
import { utcToZonedTime } from 'date-fns-tz';
import { useAppSelector } from '../../hooks/useRedux';

export default function PlanAttendance() {
  const { activeTab, onTabChange } = useTab('all');
  const { planId } = useParams();
  const { profile } = useAppSelector((state) => state.profileSlice);
  const [plan, setPlan] = useState<IPlanAttendance>();
  const [sessions, setSessions] = useState<ITrialClass[]>([]);
  const [loading, setLoading] = useState(false);
  const { timezone } = useAppSelector((state) => state.preferencesSlice);

  useEffect(() => {
    setLoading(true);
    xhr({
      url: `/t/${profile?._id}/plans/${planId}/attendance`,
      method: 'GET'
    })
      .then((res) => {
        if (res.status === 'OK') {
          setPlan(res.data);
          setLoading(false);
        }
      })
      .catch(() => {
        setLoading(false);
      });
  }, [planId]);

  function onFilter(activeTab: string) {
    if (activeTab === 'all') {
      setSessions(plan?.sessions.filter((session) => session.attendance) || []);
    } else {
      setSessions(
        plan?.sessions.filter((session) => session.attendance && session.attendance.tutor === activeTab) || []
      );
    }
  }

  useEffect(() => {
    onFilter(activeTab);
  }, [activeTab, plan]);

  if (loading) {
    return (
      <div className="w-full min-h-[164px] flex items-center justify-center">
        <img src={LOADING} className="w-14 h-14" />
      </div>
    );
  }

  if (!plan) {
    return null;
  }

  return (
    <MainContainer>
      <div className="w-full flex flex-col gap-7">
        <div className="w-full flex flex-col gap-7 bg-white p-7 rounded-2xl">
          <h1 className="text-2xl font-semibold">My Attendance </h1>
          <div className="w-full max-w-2xl border-l-4 border border-primary bg-white rounded-md p-7 flex flex-col md:flex-row gap-7">
            <div className="w-[164px] md:w-[128px] mx-auto">
              <AttendanceGuage
                percentage={getAttendacePercentage(
                  plan.sessions.filter((i) => i.attendance && i.attendance.tutor === 'present').length,
                  plan.plan.totalSessions
                )}
              />
            </div>
            <div className="w-full flex flex-col gap-4">
              <h1 className="text-sm md:text-lg text-primary font-medium">Plan Id: {plan._id}</h1>

              <div className="flex flex-row-reverse justify-end md:justify-start items-center md:items-start md:flex-col gap-2">
                <p className="text-2xl font-bold">
                  {plan.sessions.filter((session) => session.status === 'completed').length}/{plan.plan.totalSessions}
                </p>
                <p className="text-sm text-neutralGray2 font-medium">Completed classes</p>
              </div>
            </div>
            <div className="flex md:flex-col items-center gap-2">
              <div className="h-10 w-10 md:h-16 md:w-16 rounded-full overflow-hidden">
                <img
                  src={plan.plan.user.profilePic || DEFAULT_PROFILE_PIC}
                  alt="tutor_profile_pic"
                  className="w-full h-full object-cover"
                />
              </div>
              <span className="text-sm md:text-base font-normal">{plan.plan.user.name}</span>
            </div>
          </div>
        </div>

        <div className="w-full flex flex-col items-center gap-7 py-4 bg-white rounded-2xl">
          <div className="w-full px-7 border-b border-black">
            <TabBar
              value={activeTab}
              indicatorColor="bg-secondary"
              onChange={onTabChange}
              textColor="text-neutralGray1"
            >
              <TabItem label="All" value="all" />
              <TabItem label="Present" value="present" />
              <TabItem label="Absent" value="absent" />
            </TabBar>
          </div>

          <div className="w-full px-7 py-4 bg-neutralGray4 flex items-center gap-5">
            <p className="text-base font-semibold">Filters</p>
          </div>

          <div className="w-full px-7 flex flex-col gap-4">
            {sessions.length === 0 && (
              <div className="w-full flex flex-col items-center justify-center gap-7 py-10">
                <h1 className="text-2xl font-semibold">{`No Results Found!`}</h1>
              </div>
            )}
            {sessions.map((session) => (
              <ClassCard
                _id={session._id}
                key={session._id}
                type="plan"
                category={session.category}
                endTime={utcToZonedTime(session.scheduledTime.end, timezone)}
                startTime={utcToZonedTime(session.scheduledTime.start, timezone)}
                name={plan.plan.user?.name || ''}
                profilePic={plan.plan.user?.profilePic || DEFAULT_PROFILE_PIC}
                status={session.status}
                showCancelBtn={false}
                showJoinBtn={false}
                showRescheduleBtn={false}
                attendance={session.attendance && session.attendance.tutor}
              />
            ))}
          </div>
        </div>
      </div>
    </MainContainer>
  );
}
